import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  PlayCircle, Clock, Users, BookOpen, FileText, 
  HelpCircle, CheckCircle, Lock, ArrowLeft, Loader2, 
  ShoppingCart, Tag, Eye, Sparkles, Award, Package,
  DollarSign, Percent, ChevronRight, Mail, Play, X,
  Volume2, Pause, MessageCircle, Send
} from 'lucide-react';
import ShareButtons from '../components/ShareButtons';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CourseDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [previewLesson, setPreviewLesson] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [pendingPreviewLesson, setPendingPreviewLesson] = useState(null);
  const [leadForm, setLeadForm] = useState({ name: '', email: '' });
  const [leadSubmitting, setLeadSubmitting] = useState(false);

  // Audio state
  const [audioLessonId, setAudioLessonId] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // AI Coach state
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachMessages, setCoachMessages] = useState([]);
  const [coachInput, setCoachInput] = useState('');
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachSessionId, setCoachSessionId] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    fetchCourse();
    
    // Check for purchase redirect
    if (searchParams.get('purchased') === 'true') {
      verifyPurchase();
    }
  }, [id, user, token, searchParams]);

  const fetchCourse = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/courses/${id}/details`, { headers });
      setCourse(response.data);
      // Track view
      axios.post(`${API}/courses/${id}/view`).catch(() => {});
    } catch (error) {
      // Fall back to basic course endpoint
      try {
        const response = await axios.get(`${API}/courses/${id}`);
        setCourse(response.data);
      } catch (e) {
        toast.error('Course not found');
        navigate('/courses');
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyPurchase = async () => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && token) {
      try {
        await axios.get(`${API}/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase successful! You now have access.');
        fetchCourse();
      } catch (error) {
        console.error('Verify error:', error);
      }
    }
  };

  const applyCoupon = async () => {
    if (!couponCode) return;
    try {
      const response = await axios.post(`${API}/coupons/validate?code=${couponCode}&course_id=${id}`);
      if (response.data.valid) {
        setAppliedCoupon(response.data);
        toast.success(`Coupon applied! ${response.data.discount_percent}% off`);
      } else {
        toast.error(response.data.message || 'Invalid coupon');
        setAppliedCoupon(null);
      }
    } catch (error) {
      toast.error('Invalid coupon code');
      setAppliedCoupon(null);
    }
  };

  const handleBundlePurchase = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setPurchasing('bundle');
    try {
      const response = await axios.post(`${API}/checkout/purchase-course`, {
        plan: 'course',
        course_id: id,
        origin_url: window.location.origin,
        coupon_code: appliedCoupon?.code || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setPurchasing(null);
    }
  };

  const handleFreeEnroll = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setPurchasing('free');
    try {
      await axios.post(`${API}/checkout/enroll-free`, { course_id: id }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Enrolled successfully! Start learning now.');
      fetchCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enrollment failed');
    } finally {
      setPurchasing(null);
    }
  };

  const handleLessonPurchase = async (lessonId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setPurchasing(lessonId);
    try {
      const response = await axios.post(`${API}/checkout/purchase-lesson`, {
        lesson_id: lessonId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setPurchasing(null);
    }
  };

  const openPreview = (lesson) => {
    if (!user) {
      setPendingPreviewLesson(lesson);
      setShowLeadModal(true);
      return;
    }
    setPreviewLesson(lesson);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadSubmitting(true);
    try {
      await axios.post(`${API}/leads/capture`, {
        email: leadForm.email,
        name: leadForm.name,
        course_slug: course?.slug || id,
        course_title: course?.title || ''
      });
      toast.success('Check your email for an exclusive 50% off discount!');
      setShowLeadModal(false);
      navigate(`/register?email=${encodeURIComponent(leadForm.email)}&name=${encodeURIComponent(leadForm.name)}&redirect=/courses/${id}`);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLeadSubmitting(false);
    }
  };

  const startLearning = (lessonSlug) => {
    if (!user) {
      setPendingPreviewLesson(null);
      setShowLeadModal(true);
      return;
    }
    if (course?.is_platform_course && course?.tier !== 'premium') {
      navigate(`/training/lesson/${lessonSlug}`);
    } else {
      navigate(`/learn/${id}`);
    }
  };

  // ── Audio Functions ──
  const fetchAudio = async (lessonId) => {
    if (audioLoading) return;
    setAudioLoading(lessonId);
    try {
      const { data } = await axios.get(`${API}/courses/${id}/lessons/${lessonId}/audio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.audio_url) {
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.audio_url}`;
        if (audioRef.current) audioRef.current.pause();
        setAudioUrl(fullUrl);
        setAudioLessonId(lessonId);
        setIsPlaying(false);
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
          }
        }, 100);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate audio');
    } finally { setAudioLoading(null); }
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); setIsPlaying(false); }
    else { audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {}); }
  };

  // ── AI Coach Functions ──
  const sendCoachMessage = async () => {
    const msg = coachInput.trim();
    if (!msg || coachLoading) return;
    setCoachInput('');
    setCoachMessages(prev => [...prev, { role: 'user', content: msg }]);
    setCoachLoading(true);
    try {
      const { data } = await axios.post(`${API}/academy/coach/chat`, {
        message: msg,
        session_id: coachSessionId,
        course_slug: course?.slug || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCoachSessionId(data.session_id);
      setCoachMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (err) {
      setCoachMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.response?.data?.detail || 'Coach error'}` }]);
    } finally { setCoachLoading(false); }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [coachMessages]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!course) return null;

  const totalDuration = course.lessons?.reduce((acc, l) => acc + (l.duration_minutes || 0), 0) || 0;
  const hours = Math.floor(totalDuration / 60);
  const minutes = totalDuration % 60;
  
  const hasFullAccess = course.has_full_access;
  const lessonPrice = course.lesson_price;
  const bundlePrice = course.price;
  const totalIfBoughtSeparately = (course.lessons?.length || 0) * (lessonPrice || 0);
  const savings = totalIfBoughtSeparately - bundlePrice;
  const savingsPercent = totalIfBoughtSeparately > 0 ? Math.round((savings / totalIfBoughtSeparately) * 100) : 0;

  const levelColors = {
    beginner: 'bg-green-100 text-green-800',
    intermediate: 'bg-yellow-100 text-yellow-800',
    advanced: 'bg-red-100 text-red-800'
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return <PlayCircle className="w-4 h-4" />;
      case 'text': return <FileText className="w-4 h-4" />;
      case 'markdown': return <FileText className="w-4 h-4" />;
      case 'quiz': return <HelpCircle className="w-4 h-4" />;
      default: return <BookOpen className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="course-detail-page">
      <Navbar />

      {/* Hero Section */}
      <div className="pt-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Button 
            variant="ghost" 
            className="text-slate-400 hover:text-white mb-6 -ml-4"
            onClick={() => navigate('/courses')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to courses
          </Button>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="flex flex-wrap gap-2">
                <Badge className={`${levelColors[course.level]} border-0`}>
                  {course.level}
                </Badge>
                <Badge variant="outline" className="border-slate-600 text-slate-300">
                  {course.category}
                </Badge>
                {course.is_platform_course && (
                  <Badge className="bg-primary/20 text-primary border-primary/30">
                    <Sparkles className="w-3 h-3 mr-1" />
                    AISkillFlow Original
                  </Badge>
                )}
              </div>

              <div className="flex items-start justify-between gap-4">
                <h1 className="font-heading text-3xl md:text-4xl font-bold">
                  {course.title}
                </h1>
                <ShareButtons title={course.title} description={course.description} />
              </div>

              <p className="text-lg text-slate-300 leading-relaxed">
                {course.description}
              </p>

              <div className="flex flex-wrap items-center gap-6 text-slate-400">
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {course.students_count || 0} students
                </span>
                <span className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  {course.lessons?.length || 0} lessons
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`} total
                </span>
                <span className="flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Certificate
                </span>
              </div>

              <p className="text-slate-400">
                Created by <span className="text-white font-medium">{course.instructor_name}</span>
              </p>
            </div>

            {/* Pricing Card */}
            <div className="lg:col-span-1">
              <Card className="overflow-hidden sticky top-24 border-0 shadow-2xl">
                <div className="aspect-video relative">
                  <img 
                    src={course.thumbnail_url} 
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                  {hasFullAccess && (
                    <div className="absolute inset-0 bg-green-600/90 flex items-center justify-center">
                      <div className="text-center text-white">
                        <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                        <span className="font-semibold">Full Access</span>
                      </div>
                    </div>
                  )}
                </div>
                <CardContent className="p-6 space-y-4">
                  {hasFullAccess ? (
                    <>
                      <p className="text-center text-green-600 font-medium">You own this course!</p>
                      <Button 
                        className="w-full bg-primary hover:bg-primary/90 h-12"
                        onClick={() => {
                          const firstLesson = course.lessons?.[0];
                          if (firstLesson) startLearning(firstLesson.slug);
                        }}
                        data-testid="start-learning-btn"
                      >
                        <PlayCircle className="w-5 h-5 mr-2" />
                        Start Learning
                      </Button>
                    </>
                  ) : (!bundlePrice || bundlePrice === 0) ? (
                    <>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                        <span className="text-2xl font-bold text-green-700">FREE</span>
                        <p className="text-sm text-green-600 mt-1">All {course.lessons?.length} lessons included</p>
                      </div>
                      <Button
                        className="w-full bg-green-600 hover:bg-green-700 h-12 text-white"
                        onClick={handleFreeEnroll}
                        disabled={purchasing === 'free'}
                        data-testid="enroll-free-btn"
                      >
                        {purchasing === 'free' ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Enrolling...
                          </>
                        ) : (
                          <>
                            <BookOpen className="w-5 h-5 mr-2" />
                            {user ? 'Enroll Free' : 'Log In to Enroll Free'}
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* Bundle Pricing */}
                      {lessonPrice && bundlePrice && (
                        <div className="bg-gradient-to-r from-primary/10 to-purple-100 rounded-lg p-4 border border-primary/20">
                          <div className="flex items-center gap-2 mb-2">
                            <Package className="w-5 h-5 text-primary" />
                            <span className="font-semibold text-slate-900">Bundle Deal</span>
                            <Badge className="bg-green-100 text-green-800 ml-auto">
                              Save {savingsPercent}%
                            </Badge>
                          </div>
                          <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-bold text-slate-900">${bundlePrice}</span>
                            <span className="text-slate-500 line-through">${totalIfBoughtSeparately}</span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">
                            All {course.lessons?.length} lessons included
                          </p>
                        </div>
                      )}

                      {/* Coupon Input */}
                      <div className="flex gap-2">
                        <Input
                          placeholder="Coupon code"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          className="uppercase"
                          data-testid="coupon-input"
                        />
                        <Button variant="outline" onClick={applyCoupon} data-testid="apply-coupon-btn">
                          <Tag className="w-4 h-4" />
                        </Button>
                      </div>

                      {appliedCoupon && (
                        <div className="flex items-center gap-2 text-green-600 text-sm">
                          <Percent className="w-4 h-4" />
                          {appliedCoupon.discount_percent}% discount applied!
                        </div>
                      )}

                      <Button 
                        className="w-full bg-primary hover:bg-primary/90 h-12"
                        onClick={handleBundlePurchase}
                        disabled={purchasing === 'bundle'}
                        data-testid="buy-bundle-btn"
                      >
                        {purchasing === 'bundle' ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Buy Complete Course - ${appliedCoupon 
                              ? (bundlePrice * (1 - appliedCoupon.discount_percent / 100)).toFixed(2)
                              : bundlePrice
                            }
                          </>
                        )}
                      </Button>

                      {lessonPrice && (
                        <p className="text-center text-sm text-slate-500">
                          Or buy individual lessons for ${lessonPrice} each
                        </p>
                      )}

                      <p className="text-xs text-center text-slate-500">
                        30-day money-back guarantee
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Lesson Modal */}
      <Dialog open={!!previewLesson} onOpenChange={() => setPreviewLesson(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewLesson?.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {previewLesson?.content_type === 'markdown' && previewLesson?.content && (
              <div className="prose prose-slate max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {previewLesson.content.substring(0, 2000) + (previewLesson.content.length > 2000 ? '\n\n...(Preview truncated)' : '')}
                </ReactMarkdown>
              </div>
            )}
            {previewLesson?.content_type === 'video' && (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                {previewLesson.content.includes('youtube') ? (
                  <iframe
                    src={previewLesson.content.replace('watch?v=', 'embed/')}
                    className="w-full h-full"
                    allowFullScreen
                  />
                ) : (
                  <video src={previewLesson.content} controls className="w-full h-full" />
                )}
              </div>
            )}
            {previewLesson?.content_type === 'text' && (
              <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: previewLesson.content?.replace(/\n/g, '<br/>') || '' }} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Course Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-semibold mb-6">Course Curriculum</h2>
            
            {course.lessons?.length > 0 ? (
              <div className="space-y-3">
                {course.lessons.sort((a, b) => (a.order || 0) - (b.order || 0)).map((lesson, index) => {
                  const hasAccess = lesson.has_access || hasFullAccess || lesson.is_preview;
                  const isLocked = !hasAccess;
                  
                  return (
                    <Card 
                      key={lesson.id}
                      className={`border transition-all ${isLocked ? 'bg-slate-50' : 'hover:border-primary/50 hover:shadow-md'}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {/* Lesson Number */}
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            lesson.quiz_passed ? 'bg-green-100' :
                            hasAccess ? 'bg-primary/10' : 'bg-slate-200'
                          }`}>
                            {lesson.quiz_passed ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : isLocked ? (
                              <Lock className="w-4 h-4 text-slate-400" />
                            ) : (
                              <span className="text-sm font-semibold text-primary">{index + 1}</span>
                            )}
                          </div>

                          {/* Lesson Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className={`font-medium ${isLocked ? 'text-slate-500' : 'text-slate-900'}`}>
                                {lesson.title}
                              </h3>
                              {lesson.tool_name && (
                                <Badge variant="outline" className="text-xs">
                                  {lesson.tool_name}
                                </Badge>
                              )}
                              {lesson.is_preview && !hasFullAccess && (
                                <Badge className="bg-blue-100 text-blue-800 text-xs">
                                  <Eye className="w-3 h-3 mr-1" />
                                  Free Preview
                                </Badge>
                              )}
                              {lesson.has_quiz && (
                                <Badge variant="outline" className="text-xs">
                                  <HelpCircle className="w-3 h-3 mr-1" />
                                  Quiz
                                </Badge>
                              )}
                            </div>
                            {lesson.short_summary && (
                              <p className={`text-sm mt-1 truncate ${isLocked ? 'text-slate-400' : 'text-slate-600'}`}>
                                {lesson.short_summary}
                              </p>
                            )}
                          </div>

                          {/* Duration & Actions */}
                          <div className="flex items-center gap-3 flex-shrink-0">
                            {lesson.duration_minutes > 0 && (
                              <span className="text-sm text-slate-500 flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {lesson.duration_minutes}m
                              </span>
                            )}
                            
                            {hasAccess ? (
                              <div className="flex items-center gap-2">
                                {/* Audio Listen Button */}
                                {hasFullAccess && (
                                  audioLessonId === lesson.id && audioUrl ? (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={toggleAudio}
                                      className="text-indigo-600 hover:text-indigo-700"
                                      data-testid={`audio-toggle-${lesson.id}`}
                                    >
                                      {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => fetchAudio(lesson.id)}
                                      disabled={audioLoading === lesson.id}
                                      className="text-slate-500 hover:text-indigo-600"
                                      data-testid={`audio-btn-${lesson.id}`}
                                    >
                                      {audioLoading === lesson.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
                                    </Button>
                                  )
                                )}
                                <Button
                                  size="sm"
                                  variant={lesson.is_preview && !hasFullAccess ? "outline" : "default"}
                                  onClick={() => {
                                    if (lesson.is_preview && !hasFullAccess) {
                                      openPreview(lesson);
                                    } else {
                                      startLearning(lesson.slug);
                                    }
                                  }}
                                  data-testid={`lesson-${lesson.id}-btn`}
                                >
                                  {lesson.is_preview && !hasFullAccess ? (
                                    <>
                                      <Eye className="w-4 h-4 mr-1" />
                                      Preview
                                    </>
                                  ) : (
                                    <>
                                      <PlayCircle className="w-4 h-4 mr-1" />
                                      {lesson.quiz_passed ? 'Review' : 'Start'}
                                    </>
                                  )}
                                </Button>
                              </div>
                            ) : lessonPrice ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleLessonPurchase(lesson.id)}
                                disabled={purchasing === lesson.id}
                                data-testid={`buy-lesson-${lesson.id}-btn`}
                              >
                                {purchasing === lesson.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <DollarSign className="w-4 h-4 mr-1" />
                                    ${lessonPrice}
                                  </>
                                )}
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" disabled>
                                <Lock className="w-4 h-4 mr-1" />
                                Locked
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No lessons added yet</p>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="p-6">
              <h3 className="font-heading font-semibold text-lg mb-4">What you'll learn</h3>
              <ul className="space-y-3">
                {(course.lessons || []).slice(0, 5).map((lesson, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{lesson.tool_name || lesson.title}</span>
                  </li>
                ))}
                {(course.lessons?.length || 0) > 5 && (
                  <li className="text-sm text-slate-500 pl-8">
                    +{course.lessons.length - 5} more topics
                  </li>
                )}
              </ul>
            </Card>

            <Card className="p-6">
              <h3 className="font-heading font-semibold text-lg mb-4">This course includes</h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-slate-400" />
                  {course.lessons?.length || 0} comprehensive lessons
                </li>
                <li className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-400" />
                  {hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`} of content
                </li>
                <li className="flex items-center gap-3">
                  <HelpCircle className="w-5 h-5 text-slate-400" />
                  Quizzes & exercises
                </li>
                <li className="flex items-center gap-3">
                  <Award className="w-5 h-5 text-slate-400" />
                  Certificate of completion
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-slate-400" />
                  Lifetime access
                </li>
              </ul>
            </Card>

            {/* Pricing Comparison */}
            {lessonPrice && bundlePrice && !hasFullAccess && (
              <Card className="p-6 bg-gradient-to-br from-primary/5 to-purple-50 border-primary/20">
                <h3 className="font-heading font-semibold text-lg mb-4">Pricing Options</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Individual lessons</span>
                    <span className="font-medium">${lessonPrice} each</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">All {course.lessons?.length} lessons</span>
                    <span className="text-slate-500 line-through">${totalIfBoughtSeparately}</span>
                  </div>
                  <hr className="border-slate-200" />
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-slate-900">Bundle price</span>
                    <span className="font-bold text-xl text-primary">${bundlePrice}</span>
                  </div>
                  <div className="text-center">
                    <Badge className="bg-green-100 text-green-800">
                      You save ${savings.toFixed(2)} ({savingsPercent}%)
                    </Badge>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Hidden Audio Element */}
      {audioUrl && <audio ref={audioRef} src={audioUrl} onEnded={() => setIsPlaying(false)} />}

      {/* AI Coach Floating Button + Panel */}
      {user && hasFullAccess && (
        <>
          {!coachOpen && (
            <button
              onClick={() => setCoachOpen(true)}
              className="fixed bottom-6 right-6 z-40 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full p-4 shadow-lg transition-all hover:scale-105"
              data-testid="ai-coach-fab"
            >
              <MessageCircle className="w-6 h-6" />
            </button>
          )}
          {coachOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) setCoachOpen(false); }}>
              <div className="w-[440px] h-[540px] bg-white rounded-2xl shadow-2xl border flex flex-col overflow-hidden" data-testid="ai-coach-panel" style={{maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 32px)'}}>
                <div className="p-3 bg-indigo-600 text-white flex items-center justify-between rounded-t-2xl flex-shrink-0">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  <span className="font-medium text-sm">AI Coach</span>
                </div>
                <button onClick={() => setCoachOpen(false)} className="text-white/70 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <ScrollArea className="flex-1 p-3">
                {coachMessages.length === 0 && (
                  <div className="text-center text-slate-400 mt-6 px-4">
                    <MessageCircle className="w-8 h-8 mx-auto mb-3 opacity-50" />
                    <p className="text-sm mb-4">Ask me anything about this course!</p>
                    <div className="space-y-2">
                      {['Summarize this course', 'What will I learn?', 'Give me study tips'].map((q) => (
                        <button
                          key={q}
                          onClick={() => { setCoachInput(q); }}
                          className="block w-full text-left text-xs p-2 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {coachMessages.map((msg, i) => (
                  <div key={i} className={`mb-3 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                    <div className={`inline-block max-w-full p-3 rounded-lg text-sm ${
                      msg.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-800'
                    }`} style={{overflowWrap: 'break-word', wordBreak: 'break-word'}}>
                      {msg.role === 'assistant' ? (
                        <div className="prose prose-sm max-w-none [&>*]:overflow-hidden" style={{overflowWrap: 'break-word', wordBreak: 'break-word'}}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                        </div>
                      ) : msg.content}
                    </div>
                  </div>
                ))}
                {coachLoading && (
                  <div className="flex items-center gap-2 text-slate-400 text-sm mb-3">
                    <Loader2 className="w-3 h-3 animate-spin" /> Thinking...
                  </div>
                )}
                <div ref={chatEndRef} />
              </ScrollArea>
              <div className="p-3 border-t">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={coachInput}
                    onChange={(e) => setCoachInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendCoachMessage()}
                    placeholder="Ask the AI Coach..."
                    className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    data-testid="coach-input"
                  />
                  <Button
                    size="icon"
                    onClick={sendCoachMessage}
                    disabled={!coachInput.trim() || coachLoading}
                    className="bg-indigo-600 hover:bg-indigo-700 h-9 w-9"
                    data-testid="coach-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          )}
        </>
      )}

      {/* Lead Capture Modal */}
      {showLeadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="lead-capture-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white relative">
              <button
                onClick={() => setShowLeadModal(false)}
                className="absolute top-4 right-4 text-white/70 hover:text-white"
                data-testid="lead-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
              <h3 className="font-heading text-xl font-bold">Start Your Free Preview</h3>
              <p className="text-indigo-200 text-sm mt-1">Register to access free lessons + get 50% off your first purchase</p>
            </div>
            <form onSubmit={handleLeadSubmit} className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <p className="text-amber-800 font-semibold text-sm">50% OFF your first purchase — just for signing up!</p>
                <p className="text-amber-600 text-xs mt-0.5">Discount code sent to your email instantly</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-name">Your name</Label>
                <Input
                  id="lead-name"
                  placeholder="Enter your name"
                  value={leadForm.name}
                  onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                  required
                  data-testid="lead-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-email">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="lead-email"
                    type="email"
                    placeholder="you@example.com"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    required
                    className="pl-10"
                    data-testid="lead-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-indigo-600 hover:bg-indigo-700"
                disabled={leadSubmitting}
                data-testid="lead-submit-btn"
              >
                {leadSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {leadSubmitting ? 'Processing...' : 'Start Free Preview'}
              </Button>
              <p className="text-xs text-slate-400 text-center">
                Already have an account? <a href="/login" className="text-indigo-600 hover:underline">Sign in</a>
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetail;
